/* ═══════════════════════════════════════════════════════════════
   Timesheets rebuild — hour-cell copy/paste triad context menu.
   Portal + position:fixed + mousedown-outside dismiss (DESIGN.md §5).
   ═══════════════════════════════════════════════════════════════ */

import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Copy, Scissors, ClipboardPaste } from 'lucide-react';
import { Icon } from '../../components/ui/Icon';
import { rem } from '../../lib/rem';
import { appLayerProps } from '../../components/ui/app-layer';

export interface CellContextMenuProps {
  x: number;
  y: number;
  canPaste: boolean;
  onCopy: () => void;
  onCut: () => void;
  onPaste: () => void;
  onClose: () => void;
}

export function CellContextMenu({
  x,
  y,
  canPaste,
  onCopy,
  onCut,
  onPaste,
  onClose,
}: CellContextMenuProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onMouseDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const items: { label: string; icon: React.ReactNode; action: () => void; disabled?: boolean }[] =
    [
      { label: 'Copy', icon: <Icon icon={Copy} size="md" />, action: onCopy },
      { label: 'Cut', icon: <Icon icon={Scissors} size="md" />, action: onCut },
      {
        label: 'Paste',
        icon: <Icon icon={ClipboardPaste} size="md" />,
        action: onPaste,
        disabled: !canPaste,
      },
    ];

  return createPortal(
    <div
      {...appLayerProps}
      ref={ref}
      role="menu"
      style={{
        position: 'fixed',
        left: x,
        top: y,
        background: 'var(--sf)',
        border: '1px solid var(--bd)',
        borderRadius: 'var(--radius-md)',
        boxShadow: '0 8px 24px rgba(0,0,0,.18)',
        padding: 'var(--space-1) 0',
        minWidth: rem(140),
        zIndex: 1000,
      }}
    >
      {items.map((item) => (
        <button
          key={item.label}
          role="menuitem"
          disabled={item.disabled}
          onClick={() => {
            if (item.disabled) return;
            onClose();
            item.action();
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            width: '100%',
            textAlign: 'left',
            padding: `${rem(7)} ${rem(14)}`,
            fontSize: 'var(--font-size-base)',
            fontWeight: 500,
            background: 'none',
            border: 'none',
            cursor: item.disabled ? 'not-allowed' : 'pointer',
            color: item.disabled ? 'var(--tm)' : 'var(--tx)',
            opacity: item.disabled ? 0.5 : 1,
          }}
          onMouseEnter={(e) => {
            if (!item.disabled) e.currentTarget.style.background = 'var(--sa)';
          }}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
        >
          {item.icon}
          {item.label}
        </button>
      ))}
    </div>,
    document.body
  );
}
